/**
 * Admin Console → Workspaces → one workspace (gods only).
 *
 * Shows the workspace's members with their roles (owner, admins, editors,
 * viewers and pending invitations) and, on SaaS, the per-workspace feature
 * toggles: Entity Extraction, Summarization, Content Generation and the three
 * agentic autonomy levels.  Classification and "no tool calling" are always
 * on and are not shown as toggles.  Every switch is saved immediately through
 * `PUT /platform/workspaces/{id}/features`; a refused save flips it back.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Button, DataTable, SparkIcon, TableToolbarContent, TagColored, Text, Toggle, sparkToast } from '@spark';
import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import { OWNER_ROLE_LABEL, ROLE_TAG_VARIANT } from '@/features/users/components/UserRoleSelect.jsx';
import { formatDateTime } from '@/shared/utils/helpers';
import { WORKSPACE_FEATURE_GROUPS } from '@/shared/domain/workspaceFeatures';
import { fetchWorkspaceDetail, setWorkspaceFeatures } from '../api/adminApi';
import { ENTER_WORKSPACE_LABEL, WORKSPACE_STATUS_TAG } from '../constants';

const EMPTY_CELL = '-';
export const PENDING_ACCEPTANCE = 'Pending acceptance';
export const FEATURES_TITLE = 'Enabled features';
export const FEATURES_HINT =
    'Classification and "no tool calling" are always available. Switch on what this workspace may use beyond that.';

const emailLocalPart = (email) => (email || '').split('@')[0] || '';

/**
 * One feature switch.  The checked state is owned by the parent so a refused
 * save can roll it back; `saving` disables the whole group while a request
 * is in flight so two quick flips cannot race each other.
 */
function FeatureToggle({ feature, enabled, saving, onToggle }) {
    return (
        <div className="admin-feature-row">
            <Toggle
                id={`feature-${feature.id}`}
                label={feature.label}
                labelPosition="end"
                size="medium"
                active={enabled}
                disabled={saving}
                onChange={(event) => onToggle(feature.id, event.target.checked)}
            />
        </div>
    );
}

export default function WorkspaceDetail({
    workspaceId,
    featuresEditable,
    isActive,
    entering,
    onBack,
    onEnter,
    onWorkspaceChanged,
}) {
    const [workspace, setWorkspace] = useState(null);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [features, setFeatures] = useState([]);
    const [savingFeatures, setSavingFeatures] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        const result = await fetchWorkspaceDetail(workspaceId);
        if (!result.ok) {
            setWorkspace(null);
            setMembers([]);
            setLoadError(result.error);
        } else {
            const ws = result.data?.workspace || null;
            setWorkspace(ws);
            setFeatures(Array.isArray(ws?.features) ? ws.features : []);
            setMembers(Array.isArray(result.data?.members) ? result.data.members : []);
        }
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => {
        load();
    }, [load]);

    const enabledSet = useMemo(() => new Set(features), [features]);

    const toggleFeature = async (featureId, enabled) => {
        if (savingFeatures || !workspace) return;
        const next = enabled ? [...new Set([...features, featureId])] : features.filter((id) => id !== featureId);
        const previous = features;
        setFeatures(next);
        setSavingFeatures(true);
        const result = await setWorkspaceFeatures(workspace.id, next);
        setSavingFeatures(false);
        if (!result.ok) {
            setFeatures(previous);
            sparkToast.error(result.error);
            return;
        }
        const saved = Array.isArray(result.data?.features) ? result.data.features : next;
        setFeatures(saved);
        setWorkspace((prev) => (prev ? { ...prev, features: saved } : prev));
        onWorkspaceChanged?.({ id: workspace.id, features: saved });
    };

    const memberColumns = [
        {
            title: 'Member',
            key: 'member',
            dataIndex: 'email',
            minWidth: 260,
            flex: 1,
            render: (_, member) => {
                const displayName = member.name || emailLocalPart(member.email);
                return (
                    <Avatar
                        size="medium"
                        type={member.picture ? 'image' : 'initials'}
                        src={member.picture || undefined}
                        alt={displayName}
                        primaryText={displayName}
                        secondaryText={member.email || ''}
                        showContent
                    />
                );
            },
        },
        {
            title: 'Role',
            key: 'role',
            dataIndex: 'role',
            width: 160,
            render: (role, member) => {
                const label = member.is_owner ? OWNER_ROLE_LABEL : role || 'Unassigned';
                return (
                    <span className="settings-users__role-cell">
                        <TagColored
                            className="settings-users__role-tag"
                            label={label}
                            variant={ROLE_TAG_VARIANT[label] || 'yellow'}
                        />
                    </span>
                );
            },
        },
        {
            title: 'Joined',
            key: 'joined_at',
            dataIndex: 'joined_at',
            width: 190,
            render: (value, member) => {
                if (member.status === 'invited') {
                    return <Text variant="base-regular" color="tertiary">{PENDING_ACCEPTANCE}</Text>;
                }
                return value ? formatDateTime(value) : EMPTY_CELL;
            },
        },
        {
            title: 'Invited by',
            key: 'invited_by',
            dataIndex: 'invited_by',
            minWidth: 200,
            render: (value) => value || EMPTY_CELL,
        },
    ];

    const title = workspace?.name || (loading ? 'Loading…' : 'Workspace');
    const statusTag = WORKSPACE_STATUS_TAG[workspace?.status] || WORKSPACE_STATUS_TAG.active;
    const membersEmptyText = loading ? 'Loading members...' : loadError || 'No members yet';

    return (
        <SettingsSectionCard
            title={(
                <span className="admin-detail-title">
                    <Button
                        variant="ghost"
                        size="small"
                        aria-label="Back to workspaces"
                        icon={<SparkIcon slug="arrow-left" size="small" />}
                        onClick={onBack}
                    />
                    <span>{title}</span>
                    {workspace ? <TagColored label={statusTag.label} variant={statusTag.variant} /> : null}
                    {isActive ? <TagColored label="Current" variant="blue" /> : null}
                </span>
            )}
            headerExtra={(
                <>
                    <Button variant="tertiary" size="medium" disabled={loading} onClick={load}>
                        Refresh
                    </Button>
                    <Button
                        variant="primary"
                        size="medium"
                        disabled={!workspace || entering}
                        loading={entering}
                        onClick={() => workspace && onEnter(workspace)}
                    >
                        {ENTER_WORKSPACE_LABEL}
                    </Button>
                </>
            )}
        >
            <div className="settings-users admin-detail">
                {workspace ? (
                    <dl className="admin-detail-facts">
                        <div>
                            <dt>Owner</dt>
                            <dd>{workspace.owner_email || EMPTY_CELL}</dd>
                        </div>
                        <div>
                            <dt>Created</dt>
                            <dd>{workspace.created_at ? formatDateTime(workspace.created_at) : EMPTY_CELL}</dd>
                        </div>
                        <div>
                            <dt>Created by</dt>
                            <dd>{workspace.created_by || EMPTY_CELL}</dd>
                        </div>
                        <div>
                            <dt>Workspace ID</dt>
                            <dd className="admin-detail-mono">{workspace.id}</dd>
                        </div>
                    </dl>
                ) : null}

                {featuresEditable && workspace ? (
                    <section className="admin-features" aria-labelledby="admin-features-title">
                        <div className="admin-features__header">
                            <Text as="h3" id="admin-features-title" variant="heading-5-semibold" color="primary">
                                {FEATURES_TITLE}
                            </Text>
                            <Text as="p" variant="base-regular" color="secondary">{FEATURES_HINT}</Text>
                        </div>
                        <div className="admin-features__groups">
                            {WORKSPACE_FEATURE_GROUPS.map((group) => (
                                <div key={group.id} className="admin-features__group" data-testid={`feature-group-${group.id}`}>
                                    <Text as="h4" variant="base-semibold" color="primary">{group.title}</Text>
                                    <Text as="p" variant="detail-regular" color="tertiary">{group.description}</Text>
                                    <div className="admin-features__list">
                                        {group.features.map((feature) => (
                                            <FeatureToggle
                                                key={feature.id}
                                                feature={feature}
                                                enabled={enabledSet.has(feature.id)}
                                                saving={savingFeatures}
                                                onToggle={toggleFeature}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                ) : null}

                <div className="settings-users__table-block">
                    <div className="settings-users__toolbar">
                        <TableToolbarContent
                            title={`Members (${members.length})`}
                            showInfoIconTitle={false}
                            showSubtitle={false}
                        />
                    </div>
                    <DataTable
                        rowKey={(record) => String(record.user_id || record.invitation_id || record.email)}
                        columns={memberColumns}
                        dataSource={members}
                        disableHoverBackground
                        pagination={null}
                        emptyText={membersEmptyText}
                    />
                </div>
            </div>
        </SettingsSectionCard>
    );
}
